import {
  ColDef,
  ColGroupDef,
  ICellEditorParams,
  NewValueParams,
  ValueFormatterParams,
  ValueSetterParams,
} from '@ag-grid-community/core';
import dayjs from 'dayjs';
import { isArray, isEqual, isString } from 'lodash-es';

import { PivotSettings } from '@models/trial-attributes/trial-attributes';

import { ZERO_HYPHEN } from '@shared/constants/format';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { AmountType, listBudgetAttributesQuery, PrepaidType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { agCostValueSetter } from '@shared/utils/ag-value-setter.utils';
import { decimalAdd } from '@shared/utils/floating-math';
import { Utils } from '@shared/utils/utils';

import { GridConfigOptions } from './grid-config-context';
import { AgPivotColumnDropdownComponent } from '../../ag-pivot-column-dropdown/ag-pivot-column-dropdown.component';
import { AgPrepaidTypeDropdownComponent } from '../../ag-prepaid-type-dropdown/ag-prepaid-type-dropdown.component';
import { AdjustmentsToPrepaidRow, PrepaidTypeLabel } from '../adjustments-to-prepaid.model';

// Helper function to create column definition
const createColumnDef = (
  field: keyof AdjustmentsToPrepaidRow,
  headerName: string,
  options: GridConfigOptions,
  additionalProps: Partial<ColDef<AdjustmentsToPrepaidRow>> = {}
): ColDef<AdjustmentsToPrepaidRow> => {
  const {
    getCellClasses,
    isCellEditable,
    onCellValueChanged,
    recalculateCells,
    tooltipValueGetter,
  } = options;

  return {
    cellClass: getCellClasses(),
    editable: (params) => isCellEditable(params),
    field,
    headerName,
    onCellValueChanged: (params: NewValueParams) => {
      recalculateCells(params);
      onCellValueChanged(params);
    },
    tooltipValueGetter: (params) => tooltipValueGetter(params),
    width: 120,
    ...additionalProps,
  };
};

export const amountTypeMap = {
  [AmountType.AMOUNT_INVESTIGATOR]: 'Investigator',
  [AmountType.AMOUNT_PASSTHROUGH]: 'Passthrough',
  [AmountType.AMOUNT_SERVICE]: 'Services',
};

const getPivotColumnHeader = (pivotSettings: GridConfigOptions['pivotSettings']): string => {
  if (!pivotSettings.primary.attribute_name) return 'Cost Category';

  let headerName = pivotSettings.primary.attribute_name;

  if (pivotSettings.secondary.attribute_name) {
    headerName += ` / ${pivotSettings.secondary.attribute_name}`;
  }

  return headerName;
};

export const getPivotColumnValues = (
  isPivotSelected: boolean,
  budgetAttributes: GridConfigOptions['budgetAttributes'],
  pivotSettings: GridConfigOptions['pivotSettings']
): string[][] => {
  let values: string[][] = [];

  if (!isPivotSelected) {
    values = Object.keys(amountTypeMap).map((amountType) => [amountType]);
  } else {
    const primaryAttributeValues =
      budgetAttributes.filter(
        ({ attribute_name }) => attribute_name === pivotSettings.primary.attribute_name
      )[0]?.attribute_values || [];

    const secondaryAttributeValues =
      budgetAttributes.filter(
        ({ attribute_name }) => attribute_name === pivotSettings.secondary.attribute_name
      )[0]?.attribute_values || [];

    if (secondaryAttributeValues.length) {
      values = primaryAttributeValues
        .map((primaryAttribute) =>
          secondaryAttributeValues.map((secondaryAttribute) => [
            primaryAttribute,
            secondaryAttribute,
          ])
        )
        .flat();
    } else {
      values = primaryAttributeValues.map((primaryAttribute) => [primaryAttribute]);
    }
  }

  // Always add blank option at the top
  return [[] as string[], ...values];
};

const createPivotColumnValueFormatter =
  (isPivotSelected: boolean) => (params: ValueFormatterParams) => {
    if (!params.value) return ZERO_HYPHEN;

    if (isString(params.value)) return params.value;

    if (isPivotSelected) {
      return params.value.length === 0 ? ZERO_HYPHEN : params.value.join(' / ');
    }

    const value = (params.value[0] || '') as keyof typeof amountTypeMap;

    return value ? amountTypeMap[value] : ZERO_HYPHEN;
  };

function parsePivotValueString(value: string): string[] {
  if (!value) return [];
  // Try JSON parse first
  try {
    const parsed = JSON.parse(value);

    if (isArray(parsed)) return parsed;
  } catch {
    // Not JSON, continue
  }

  if (value.includes(' / ')) {
    return value.split(' / ').map((s) => s.trim());
  }

  if (value.includes(',')) {
    return value.split(',').map((s) => s.trim());
  }

  return [value.trim()];
}

const createPivotColumnValueSetter =
  (
    isPivotSelected: boolean,
    budgetAttributes: listBudgetAttributesQuery[],
    pivotSettings: PivotSettings
  ) =>
  (params: ValueSetterParams) => {
    let newValue = params.newValue;

    // Get allowed options
    const allowedOptions = getPivotColumnValues(isPivotSelected, budgetAttributes, pivotSettings);

    // If array (from drag/drop or correct paste), assign if valid
    if (isArray(newValue)) {
      const found = allowedOptions.find((opt) => isEqual(opt, newValue));

      params.data.pivot_column = found ? newValue : [];

      return true;
    }

    // If string (from paste), try to parse as array
    if (isString(newValue) && newValue) {
      let arr = parsePivotValueString(newValue);

      // If not pivot, map label back to key
      if (!isPivotSelected && arr.length === 1) {
        const key = Object.keys(amountTypeMap).find(
          (k) => amountTypeMap[k as keyof typeof amountTypeMap] === arr[0]
        );

        if (key) arr = [key];
      }

      const found = allowedOptions.find((opt) => isEqual(opt, arr));

      params.data.pivot_column = found ? arr : [];

      return true;
    }

    // Fallback: blank
    params.data.pivot_column = [];

    return true;
  };

const createPivotColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { budgetAttributes, checkError, getCellClasses, pivotSettings } = options;
  const isPivotSelected = !!pivotSettings.primary.attribute_name;
  const headerName = getPivotColumnHeader(pivotSettings);

  return createColumnDef('pivot_column', headerName, options, {
    cellClass: getCellClasses(['remove-padding-in-edit-mode', 'ag-flat-dropdown']),
    cellClassRules: {
      'border-aux-error': (params) => checkError(params, 'pivot_column'),
    },
    cellEditor: AgPivotColumnDropdownComponent,
    cellEditorParams: () => ({
      budgetAttributes,
      isPivotSelected,
      pivotSettings,
    }),
    comparator: (valueA, valueB) => {
      // Handle null/undefined/empty
      if (!valueA && !valueB) return 0;

      if (!valueA) return -1;

      if (!valueB) return 1;

      // Convert strings to arrays if needed
      const strA = isArray(valueA) ? valueA.join('') : valueA;
      const strB = isArray(valueB) ? valueB.join('') : valueB;

      return Utils.alphaNumSort(strA, strB);
    },
    filterParams: {
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return ZERO_HYPHEN;

        if (isPivotSelected) {
          if (isArray(params.value)) {
            return params.value.length === 0 ? ZERO_HYPHEN : params.value.join(' / ');
          }

          return params.value;
        }

        const value = (params.value || '') as keyof typeof amountTypeMap;

        return amountTypeMap[value] || ZERO_HYPHEN;
      },
    },
    minWidth: isPivotSelected ? 225 : 175,
    sort: 'asc',
    sortIndex: 2,
    valueFormatter: createPivotColumnValueFormatter(isPivotSelected),
    valueParser: (params) => params.newValue,
    valueSetter: createPivotColumnValueSetter(isPivotSelected, budgetAttributes, pivotSettings),
  });
};

const createTotalImpactColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { checkError, getCellClasses, vendorCurrency } = options;

  return createColumnDef('total_impact_to_prepaid', 'Total Impact to Prepaid', options, {
    cellClass: getCellClasses(['text-right']),
    cellClassRules: {
      'border-aux-error': (params) =>
        checkError(params, 'total_impact_to_prepaid_increase') ||
        checkError(params, 'total_impact_to_prepaid_amortize') ||
        checkError(params, 'total_impact_to_prepaid_reclass'),
    },
    cellDataType: 'number',
    editable: false,
    filter: 'agNumberColumnFilter',
    headerTooltip: 'Total Impact to Prepaid',
    minWidth: 200,
    suppressFillHandle: true,
    valueFormatter: (params) => {
      return Utils.agCurrencyFormatter(params, vendorCurrency);
    },
    valueGetter: (params) => {
      if (!params.data) return 0;

      return decimalAdd(params.data.short_term_amount || 0, params.data.long_term_amount || 0, 2);
    },
    width: 200,
  });
};

const createShortTermColumn = (
  options: GridConfigOptions,
  overlayService: OverlayService
): ColDef<AdjustmentsToPrepaidRow> => {
  const { checkError, getCellClasses, vendorCurrency } = options;

  return createColumnDef('short_term_amount', 'Short Term', options, {
    cellClass: getCellClasses(['text-right']),
    cellClassRules: {
      'border-aux-error': (params) =>
        checkError(params, 'short_term_increase') ||
        checkError(params, 'short_term_amortize') ||
        checkError(params, 'amortize_both_blank') ||
        checkError(params, 'increase_both_blank'),
    },
    filter: 'agNumberColumnFilter',
    headerTooltip: 'Short Term',
    minWidth: 150,
    valueFormatter: (params) => {
      return Utils.agCurrencyFormatter(params, vendorCurrency);
    },
    valueSetter: (params: ValueSetterParams) =>
      agCostValueSetter(params, overlayService, MessagesConstants.MAXIMUM_2_DECIMALS_SUPPORTED),
    width: 150,
  });
};

const createLongTermColumn = (
  options: GridConfigOptions,
  overlayService: OverlayService
): ColDef<AdjustmentsToPrepaidRow> => {
  const { checkError, getCellClasses, vendorCurrency } = options;

  return createColumnDef('long_term_amount', 'Long Term', options, {
    cellClass: getCellClasses(['text-right']),
    cellClassRules: {
      'border-aux-error': (params) =>
        checkError(params, 'long_term_increase') ||
        checkError(params, 'long_term_amortize') ||
        checkError(params, 'amortize_both_blank') ||
        checkError(params, 'increase_both_blank'),
    },
    filter: 'agNumberColumnFilter',
    headerTooltip: 'Long Term',
    minWidth: 150,
    valueFormatter: (params) => {
      return Utils.agCurrencyFormatter(params, vendorCurrency);
    },
    valueSetter: (params: ValueSetterParams) =>
      agCostValueSetter(params, overlayService, MessagesConstants.MAXIMUM_2_DECIMALS_SUPPORTED),
    width: 150,
  });
};

// Prepaid type options and helpers
const prepaidTypeOptions = [
  {
    label: PrepaidTypeLabel.PREPAID_TYPE_INCREASE,
    value: PrepaidType.PREPAID_TYPE_INCREASE,
  },
  {
    label: PrepaidTypeLabel.PREPAID_TYPE_RECLASS,
    value: PrepaidType.PREPAID_TYPE_RECLASS,
  },
  {
    label: PrepaidTypeLabel.PREPAID_TYPE_AMORTIZE,
    value: PrepaidType.PREPAID_TYPE_AMORTIZE,
  },
];

function isPrepaidTypeOptionDisabled(
  option: { label: string; value: PrepaidType },
  row: AdjustmentsToPrepaidRow,
  flags: GridConfigOptions['flags'],
  currentOpenMonth: string
): boolean {
  // This logic should match the dropdown's disabled logic
  if (!row.invoice_id) return false;

  if (flags?.journalEntryApManualClearing) {
    if (option.value === PrepaidType.PREPAID_TYPE_INCREASE) {
      if (!row.payment_date) return true;

      return dayjs(row.payment_date).isAfter(dayjs(currentOpenMonth).endOf('month'));
    }
  }

  return false;
}

function parsePrepaidTypeValueString(value: string): string {
  if (!value) return '';
  // Try JSON parse first
  try {
    const parsed = JSON.parse(value);

    if (typeof parsed === 'string') return parsed;
  } catch {
    // Not JSON, continue
  }

  return value.trim();
}

const createPrepaidTypeColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { checkError, currentOpenMonth, flags, getCellClasses, tooltipValueGetter } = options;

  return createColumnDef('prepaid_type', 'Type', options, {
    cellClass: getCellClasses(['remove-padding-in-edit-mode', 'ag-flat-dropdown']),
    cellClassRules: {
      'border-aux-error': (params) => checkError(params, 'prepaid_type'),
    },
    cellEditor: AgPrepaidTypeDropdownComponent,
    cellEditorParams: (params: ICellEditorParams) => ({
      options: prepaidTypeOptions.map((option) => ({
        ...option,
        disabled: isPrepaidTypeOptionDisabled(option, params.data, flags, currentOpenMonth),
      })),
    }),
    filterParams: {
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return ZERO_HYPHEN;

        return params.value;
      },
    },
    minWidth: 140,
    tooltipValueGetter: (params) => tooltipValueGetter(params),
    valueFormatter: Utils.dashFormatter,
    valueGetter: (params) => {
      if (!params.data) return '';

      if (params.node?.rowPinned === 'bottom') {
        return params.data.prepaid_type;
      }

      return PrepaidTypeLabel[params.data.prepaid_type as keyof typeof PrepaidTypeLabel] || '';
    },
    valueParser: (params) => params.newValue,
    valueSetter: (params) => {
      if (!params.data) return false;
      let newValue = params.newValue;

      // Accept value by label or value
      if (typeof newValue === 'string' && newValue) {
        newValue = parsePrepaidTypeValueString(newValue);
        // Try to match by label first
        let matched = prepaidTypeOptions.find(
          (opt) => opt.label === newValue || opt.value === newValue
        );

        // If matched and not disabled, set
        if (
          matched &&
          !isPrepaidTypeOptionDisabled(matched, params.data, flags, currentOpenMonth)
        ) {
          params.data.prepaid_type = matched.value;

          return true;
        }
      }

      // If value is already a valid enum and not disabled
      if (
        prepaidTypeOptions.some(
          (opt) =>
            opt.value === newValue &&
            !isPrepaidTypeOptionDisabled(opt, params.data, flags, currentOpenMonth)
        )
      ) {
        params.data.prepaid_type = newValue;

        return true;
      }

      // Fallback: blank
      params.data.prepaid_type = null;

      return true;
    },
  });
};

export const prepaidImpactColumnGroup = (
  options: GridConfigOptions,
  overlayService: OverlayService
): (ColDef<AdjustmentsToPrepaidRow> | ColGroupDef<AdjustmentsToPrepaidRow>)[] => {
  return [
    {
      children: [
        createPivotColumn(options),
        createTotalImpactColumn(options),
        createShortTermColumn(options, overlayService),
        createLongTermColumn(options, overlayService),
        createPrepaidTypeColumn(options),
      ],
      headerClass: ['text-center', 'whitespace-normal', 'header-marker'],
      headerName: 'Prepaid Impact',
      suppressStickyLabel: true,
    },
  ];
};
